import { useCallback, useMemo, useRef } from 'react';
import { ToggleButton, ToggleButtonProps } from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import type { Editor } from '@tiptap/react';
import { useNotify } from 'ra-core';
import {
    AlignmentButtons,
    ClearButtons,
    ColorButtons,
    DefaultEditorOptions,
    FormatButtons,
    LevelSelect,
    LinkButtons,
    ListButtons,
    QuoteButtons,
    RichTextInput,
    RichTextInputProps,
    RichTextInputToolbar,
    useTiptapEditor,
} from 'ra-input-rich-text';
import { uploadEmailAsset } from '@/utils/email_asset_utils';
import {
    UPLOADED_IMAGE_STYLE,
    UPLOADED_IMAGE_WIDTH,
    buildEmailEditorExtensions,
} from '@/modules/quotes/emailTiptapExtensions';
import '@/modules/quotes/emailEditor.css';

type ImageAttrs = {
    src: string;
    width?: string;
    height?: string;
    alt?: string;
    style?: string;
};

function insertImage(editor: Editor, attrs: ImageAttrs) {
    (
        editor.chain().focus() as unknown as {
            setImage: (imageAttrs: ImageAttrs) => { run: () => void };
        }
    )
        .setImage(attrs)
        .run();
}

type UploadImageButtonProps = Omit<ToggleButtonProps, 'value'> & {
    onUpload: (file: File) => Promise<string>;
};

const UploadImageButton = ({
    onUpload,
    ...props
}: UploadImageButtonProps) => {
    const notify = useNotify();
    const editor = useTiptapEditor();
    const inputRef = useRef<HTMLInputElement>(null);

    const handleFile = useCallback(
        async (file: File | null | undefined) => {
            if (!file || !editor) return;
            if (!file.type.startsWith('image/')) {
                notify('Only image files are supported', { type: 'error' });
                return;
            }
            try {
                const url = await onUpload(file);
                insertImage(editor, {
                    src: url,
                    width: UPLOADED_IMAGE_WIDTH,
                    style: UPLOADED_IMAGE_STYLE,
                });
            } catch (e) {
                notify(e instanceof Error ? e.message : 'Failed to upload image', {
                    type: 'error',
                });
            }
        },
        [editor, notify, onUpload]
    );

    return (
        <>
            <input
                ref={inputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={e => {
                    void handleFile(e.target.files?.[0]);
                    e.target.value = '';
                }}
            />
            <ToggleButton
                aria-label="Upload image"
                title="Upload image"
                {...props}
                disabled={!editor || !editor.isEditable || props.disabled}
                value="upload-image"
                onClick={() => inputRef.current?.click()}
            >
                <ImageIcon fontSize="inherit" />
            </ToggleButton>
        </>
    );
};

const emailToolbar = (
    <RichTextInputToolbar>
        <LevelSelect />
        <FormatButtons />
        <ColorButtons />
        <AlignmentButtons />
        <ListButtons />
        <LinkButtons />
        <UploadImageButton onUpload={uploadEmailAsset} />
        <QuoteButtons />
        <ClearButtons />
    </RichTextInputToolbar>
);

export const EmailRichTextInput = (props: RichTextInputProps) => {
    const notify = useNotify();
    const editorRef = useRef<Editor | null>(null);

    const insertUploadedImage = useCallback(async (file: File) => {
        const url = await uploadEmailAsset(file);
        if (editorRef.current) {
            insertImage(editorRef.current, {
                src: url,
                width: UPLOADED_IMAGE_WIDTH,
                style: UPLOADED_IMAGE_STYLE,
            });
        }
        return url;
    }, []);

    const editorOptions = useMemo(
        () => ({
            ...DefaultEditorOptions,
            extensions: buildEmailEditorExtensions(),
            onCreate: ({ editor }: { editor: Editor }) => {
                editorRef.current = editor;
            },
            onDestroy: () => {
                editorRef.current = null;
            },
            editorProps: {
                attributes: {
                    class: 'email-rich-text-editor',
                },
                handleDrop: (
                    _view: unknown,
                    event: DragEvent,
                    _slice: unknown,
                    moved: boolean
                ) => {
                    if (moved || !event.dataTransfer?.files?.length) {
                        return false;
                    }
                    const file = Array.from(event.dataTransfer.files).find(item =>
                        item.type.startsWith('image/')
                    );
                    if (!file) return false;
                    event.preventDefault();
                    void insertUploadedImage(file).catch(e => {
                        notify(e instanceof Error ? e.message : 'Failed to upload image', {
                            type: 'error',
                        });
                    });
                    return true;
                },
                handlePaste: (_view: unknown, event: ClipboardEvent) => {
                    const item = Array.from(event.clipboardData?.items ?? []).find(
                        entry => entry.type.startsWith('image/')
                    );
                    if (!item) return false;
                    const file = item.getAsFile();
                    if (!file) return false;
                    event.preventDefault();
                    void insertUploadedImage(file).catch(e => {
                        notify(e instanceof Error ? e.message : 'Failed to upload image', {
                            type: 'error',
                        });
                    });
                    return true;
                },
            },
        }),
        [insertUploadedImage, notify]
    );

    return (
        <RichTextInput
            {...props}
            editorOptions={editorOptions}
            toolbar={emailToolbar}
        />
    );
};
